import * as React from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Download, Copy, Check } from 'lucide-react'
import { cn } from '@/lib/utils'

interface Column {
  key: string
  header: string
  align?: 'left' | 'center' | 'right'
}

interface TableArtifactProps {
  artifact: {
    title: string
    columns: Column[]
    data: Record<string, unknown>[]
    showRowNumbers?: boolean
    exportable?: boolean
  }
  onExport?: (format: 'csv' | 'excel') => void
}

export function TableArtifact({ artifact, onExport }: TableArtifactProps) {
  const [copied, setCopied] = React.useState(false)
  const { title, columns, data, showRowNumbers, exportable = true } = artifact

  const formatValue = (value: unknown, key: string): string => {
    if (value === null || value === undefined) return '-'

    if (typeof value === 'number') {
      // Format as currency if key suggests it's money
      const moneyKeys = ['revenue', 'total', 'amount', 'price', 'cost', 'avg']
      if (moneyKeys.some((k) => key.toLowerCase().includes(k)) && value > 100) {
        return `RM ${value.toLocaleString()}`
      }
      return value.toLocaleString()
    }

    if (value instanceof Date) {
      return value.toLocaleDateString('en-MY')
    }

    return String(value)
  }

  const handleCopy = async () => {
    const header = columns.map((c) => c.header).join('\t')
    const rows = data.map((row) =>
      columns.map((c) => formatValue(row[c.key], c.key)).join('\t')
    )
    await navigator.clipboard.writeText([header, ...rows].join('\n'))
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border bg-muted/30">
        <h3 className="font-medium text-sm">{title}</h3>
      </div>

      {/* Table */}
      <div className="max-h-80 overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {showRowNumbers && (
                <TableHead className="w-12 text-center">#</TableHead>
              )}
              {columns.map((col) => (
                <TableHead
                  key={col.key}
                  className={cn(col.align === 'right' && 'text-right')}
                >
                  {col.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((row, i) => (
              <TableRow key={i}>
                {showRowNumbers && (
                  <TableCell className="text-center text-muted-foreground">
                    {i + 1}
                  </TableCell>
                )}
                {columns.map((col) => (
                  <TableCell
                    key={col.key}
                    className={cn(col.align === 'right' && 'text-right')}
                  >
                    {formatValue(row[col.key], col.key)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Actions */}
      {exportable && (
        <div className="flex gap-2 px-4 py-3 border-t border-border bg-muted/20">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onExport?.('csv')}
          >
            <Download className="h-3.5 w-3.5 mr-1.5" />
            CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onExport?.('excel')}
          >
            <Download className="h-3.5 w-3.5 mr-1.5" />
            Excel
          </Button>
          <Button variant="outline" size="sm" onClick={handleCopy}>
            {copied ? (
              <Check className="h-3.5 w-3.5 mr-1.5 text-green-500" />
            ) : (
              <Copy className="h-3.5 w-3.5 mr-1.5" />
            )}
            {copied ? 'Copied!' : 'Copy'}
          </Button>
        </div>
      )}
    </div>
  )
}
